<template>
  <aside class="app-sidebar" :class="{ 'app-sidebar--collapsed': isCollapsed }">
    <!-- Brand -->
    <div class="sidebar-brand">
      <img class="sidebar-brand__logo" :src="logoSrc" alt="VINPROP" />
      <Transition name="fade">
        <span v-if="!isCollapsed" class="sidebar-brand__badge">CRM</span>
      </Transition>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <div class="nav-section" v-for="section in mainNavSections" :key="section.id">
        <Transition name="fade">
          <h3 v-if="!isCollapsed && section.title" class="nav-section-title">
            {{ section.title }}
          </h3>
        </Transition>

        <SidebarNavItem
          v-for="item in section.items"
          :key="item.path ?? item.label"
          :item="item"
          :collapsed="isCollapsed"
          :active-path="activePath"
          @expand-sidebar="expandSidebar"
        />
      </div>
    </nav>

    <!-- Footer -->
    <div class="sidebar-footer">
      <div class="sidebar-footer__divider" />

      <SidebarNavItem
        v-if="settingsItem"
        :item="settingsItem"
        :collapsed="isCollapsed"
        :active-path="activePath"
        link-id="settings-nav-link"
        @expand-sidebar="expandSidebar"
        @navigate="handleSettingsClick"
      />

      <!-- Theme toggle -->
      <div class="theme-toggle">
        <button
          type="button"
          class="theme-toggle__btn"
          v-tooltip="isCollapsed ? themeLabel : undefined"
          @click="toggleTheme"
        >
          <SunIcon v-if="!isDark" :size="19" />
          <MoonIcon v-else :size="19" />
          <Transition name="slide-fade">
            <span v-if="!isCollapsed" class="theme-toggle__label">{{ themeLabel }}</span>
          </Transition>
        </button>
        <AppSwitch
          v-if="!isCollapsed"
          :model-value="isDark"
          size="small"
          :aria-label="themeLabel"
          @update:model-value="toggleTheme"
        />
      </div>

      <!-- Collapse -->
      <button
        type="button"
        class="sidebar-collapse"
        :aria-label="t('common.actions.collapseSidebar')"
        @click="toggleCollapse"
      >
        <ChevronLeftIcon v-if="!isCollapsed" :size="18" />
        <ChevronRightIcon v-else :size="18" />
      </button>
    </div>

    <!-- Comic-style Speech Bubble (Teleported) -->
    <Teleport to="body">
      <Transition name="bounce-in">
        <div
          v-if="showLanguageHint && !isCollapsed && bubblePosition"
          class="language-hint-bubble"
          :style="bubblePosition"
          @click="dismissHint"
        >
          <div class="bubble-content">
            <span class="bubble-emoji">🌐</span>
            <p class="bubble-text">{{ $t('common.hints.languageSettings') }}</p>
          </div>
          <div class="bubble-arrow"></div>
        </div>
      </Transition>
    </Teleport>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTheme } from '../composables/useTheme'
import AppSwitch from './AppSwitch.vue'
import SidebarNavItem from './SidebarNavItem.vue'
import { buildSidebarNav, resolveActivePath } from './sidebarNav'
import { useDashboardMetrics } from '@/features/dashboard/composables/useDashboardMetrics'
import { usePermissions } from '@/features/auth/composables/usePermissions'
import { useCompanyVerificationsStore } from '@/features/company-verifications/stores/companyVerifications.store'
import vinpropLogoBlue from '@/assets/vinprop-logo-blue.png'
import vinpropLogoWhite from '@/assets/vinprop-logo-white.png'
import { MoonIcon, SunIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-vue-next'

export type { NavItem, NavSection } from './sidebarNav'

interface Props {
  collapsed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
})

const emit = defineEmits<{
  'update:collapsed': [value: boolean]
}>()

const route = useRoute()
const { t } = useI18n()
const { isDark, toggleTheme } = useTheme()

// Dashboard metrics для счётчиков-бейджей
const { clientsMetrics, dealsMetrics, propertiesMetrics } = useDashboardMetrics()

const {
  canAccessDeals,
  canAccessProperties,
  canAccessDocuments,
  canAccessReports,
  canAccessRcAnalytics,
  canModerateRcMappings,
  canAccessCalendar,
  canAccessSettings,
  canAccessRoutingRules,
  canConfigurePipeline,
  canAccessAgentTickets,
  canManageAgents,
  canAccessAgents,
  canModerateCompanyVerifications,
  // DEV-1021. Менеджер каталога видит только «Объекты».
  isProjectManager,
  canManageProperties,
} = usePermissions()

// Бейдж-призыв на «Модерация компаний»: счётчик очереди «Требуют действия» с лёгким опросом;
// просрочка из того же опроса красит бейдж в алерт (DEV-1165).
const companyVerificationsStore = useCompanyVerificationsStore()
const MODERATION_POLL_INTERVAL = 30_000
let moderationPollTimer: ReturnType<typeof setInterval> | undefined

// Local state
const isCollapsed = ref(props.collapsed)

// Логотип меняется под тему (синий — для светлой, белый — для тёмной).
const logoSrc = computed(() => (isDark.value ? vinpropLogoWhite : vinpropLogoBlue))
const themeLabel = computed(() =>
  isDark.value ? t('common.labels.darkTheme') : t('common.labels.lightTheme'),
)

// Language hint state
const LANGUAGE_HINT_KEY = 'language-hint-dismissed'
const showLanguageHint = ref(!localStorage.getItem(LANGUAGE_HINT_KEY))
const bubblePosition = ref<Record<string, string> | null>(null)

const updateBubblePosition = () => {
  if (!showLanguageHint.value) {
    bubblePosition.value = null
    return
  }
  const el = document.getElementById('settings-nav-link')
  if (!el) {
    bubblePosition.value = null
    return
  }
  const rect = el.getBoundingClientRect()
  bubblePosition.value = {
    position: 'fixed',
    left: `${rect.right + 24}px`,
    top: `${rect.top + rect.height / 2}px`,
    transform: 'translateY(-50%)',
  }
}

onMounted(() => {
  setTimeout(() => {
    nextTick(updateBubblePosition)
  }, 100)
  window.addEventListener('resize', updateBubblePosition)
  window.addEventListener('scroll', updateBubblePosition, true)

  if (canModerateCompanyVerifications.value) {
    companyVerificationsStore.fetchPendingCount()
    moderationPollTimer = setInterval(
      () => companyVerificationsStore.fetchPendingCount(),
      MODERATION_POLL_INTERVAL,
    )
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateBubblePosition)
  window.removeEventListener('scroll', updateBubblePosition, true)
  if (moderationPollTimer) clearInterval(moderationPollTimer)
})

watch([showLanguageHint, isCollapsed], () => {
  setTimeout(() => {
    nextTick(updateBubblePosition)
  }, 50)
})

// Структура меню — в sidebarNav.ts; здесь только реактивные входные данные.
const nav = computed(() =>
  buildSidebarNav(
    {
      isProjectManager: isProjectManager.value,
      canManageProperties: canManageProperties.value,
      canAccessDeals: canAccessDeals.value,
      canAccessProperties: canAccessProperties.value,
      canAccessCalendar: canAccessCalendar.value,
      canAccessAgents: canAccessAgents.value,
      canAccessAgentTickets: canAccessAgentTickets.value,
      canModerateCompanyVerifications: canModerateCompanyVerifications.value,
      canManageAgents: canManageAgents.value,
      canAccessReports: canAccessReports.value,
      canAccessRcAnalytics: canAccessRcAnalytics.value,
      canModerateRcMappings: canModerateRcMappings.value,
      canAccessDocuments: canAccessDocuments.value,
      canAccessSettings: canAccessSettings.value,
      canAccessRoutingRules: canAccessRoutingRules.value,
      canConfigurePipeline: canConfigurePipeline.value,
    },
    t,
    {
      newClients: clientsMetrics.value?.newClients,
      newDeals: dealsMetrics.value?.newDeals,
      totalProperties: propertiesMetrics.value?.totalProperties,
      pendingVerifications: companyVerificationsStore.pendingCount,
      overdueVerifications: companyVerificationsStore.overdueCount,
    },
  ),
)

const mainNavSections = computed(() => nav.value.sections)
const settingsItem = computed(() => nav.value.settings)

// Подсвечиваем ровно один пункт — самый специфичный для текущего маршрута.
const activePath = computed(() =>
  resolveActivePath(
    [
      ...nav.value.sections.flatMap((s) => s.items),
      ...(nav.value.settings ? [nav.value.settings] : []),
    ],
    route.path,
  ),
)

const handleSettingsClick = () => {
  if (showLanguageHint.value) dismissHint()
}

const dismissHint = () => {
  showLanguageHint.value = false
  localStorage.setItem(LANGUAGE_HINT_KEY, 'true')
}

const setCollapsed = (value: boolean) => {
  isCollapsed.value = value
  emit('update:collapsed', value)
  localStorage.setItem('sidebar-collapsed', String(value))
}

const toggleCollapse = () => setCollapsed(!isCollapsed.value)
// Подпункты группы не видны в свёрнутом сайдбаре — раскрываем панель по клику.
const expandSidebar = () => setCollapsed(false)

watch(
  () => props.collapsed,
  (newVal) => {
    isCollapsed.value = newVal
  },
)

// Восстанавливаем состояние из localStorage
const savedCollapsed = localStorage.getItem('sidebar-collapsed')
if (savedCollapsed !== null) {
  isCollapsed.value = savedCollapsed === 'true'
}
</script>

<style scoped>
.app-sidebar {
  width: 280px;
  height: 100vh;
  background: var(--color-surface);
  border-right: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  padding: 20px 16px 18px;
  gap: 4px;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: sticky;
  top: 0;
  z-index: 40;
}

.app-sidebar--collapsed {
  width: 80px;
}

/* Brand */
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 8px 8px;
  overflow: hidden;
}

.sidebar-brand__logo {
  height: 24px;
  width: auto;
  display: block;
  flex-shrink: 0;
}

.sidebar-brand__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 7px;
  border-radius: 6px;
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-size: 0.6875rem; /* 11px */
  font-weight: 600;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.app-sidebar--collapsed .sidebar-brand {
  justify-content: center;
  padding: 4px 0 8px;
}

.app-sidebar--collapsed .sidebar-brand__logo {
  height: 30px;
  width: 30px;
  object-fit: cover;
  object-position: left center;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-section-title {
  font-size: 0.6875rem; /* 11px */
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--color-text-tertiary);
  padding: 16px 0 4px 12px;
  margin: 0;
}

/* Footer */
.sidebar-footer {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sidebar-footer__divider {
  height: 1px;
  background: var(--color-border-light);
  margin: 6px 0;
}

.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 42px;
  padding: 0 10px 0 12px;
  border-radius: var(--radius-md, 10px);
}

.theme-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
}

.theme-toggle__btn:hover {
  color: var(--color-text-primary);
}

.theme-toggle__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-collapse {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  margin-top: 2px;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.sidebar-collapse:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

/* Collapsed state */
.app-sidebar--collapsed .theme-toggle {
  justify-content: center;
  padding: 0;
}

.app-sidebar--collapsed .theme-toggle__btn {
  flex: 0;
  justify-content: center;
}

/* Comic-style Speech Bubble */
.language-hint-bubble {
  background: #ffffff;
  border: 5px solid #000000;
  border-radius: 1.5rem;
  padding: 1.25rem 1.5rem;
  min-width: 240px;
  max-width: 320px;
  box-shadow:
    6px 6px 0 rgba(0, 0, 0, 0.2),
    0 0 0 2px #000000;
  z-index: 9999;
  cursor: pointer;
  pointer-events: auto;
  animation: bubble-float 2s ease-in-out infinite;
  transition: all 0.2s;
}

.language-hint-bubble:hover {
  transform: translate(0, -50%) scale(1.05) !important;
  box-shadow:
    8px 8px 0 rgba(0, 0, 0, 0.2),
    0 0 0 2px #000000;
}

.bubble-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.bubble-emoji {
  font-size: 1.75rem;
  flex-shrink: 0;
  animation: emoji-wiggle 1s ease-in-out infinite;
}

.bubble-text {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #000000;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-transform: none;
}

@keyframes emoji-wiggle {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-5deg);
  }
  75% {
    transform: rotate(5deg);
  }
}

.bubble-arrow {
  position: absolute;
  left: -22px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 15px solid transparent;
  border-bottom: 15px solid transparent;
  border-right: 22px solid #000000;
}

.bubble-arrow::after {
  content: '';
  position: absolute;
  left: 6px;
  top: -11px;
  width: 0;
  height: 0;
  border-top: 11px solid transparent;
  border-bottom: 11px solid transparent;
  border-right: 16px solid #ffffff;
}

@keyframes bubble-float {
  0%,
  100% {
    transform: translate(0, -50%);
  }
  50% {
    transform: translate(4px, -50%);
  }
}

.bounce-in-enter-active {
  animation: bounce-in-anim 0.5s ease-out;
}

.bounce-in-leave-active {
  animation: bounce-out-anim 0.3s ease-in;
}

@keyframes bounce-in-anim {
  0% {
    opacity: 0;
    transform: translate(0, -50%) scale(0.3);
  }
  50% {
    transform: translate(0, -50%) scale(1.05);
  }
  70% {
    transform: translate(0, -50%) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translate(0, -50%) scale(1);
  }
}

@keyframes bounce-out-anim {
  0% {
    opacity: 1;
    transform: translate(0, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(0, -50%) scale(0.3);
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

/* Scrollbar */
.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: var(--color-border-light);
  border-radius: 2px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: var(--color-border-default);
}
</style>
